import { defineComponent as _defineComponent } from 'vue';
import { library } from "@fortawesome/fontawesome-svg-core";
import { faCaretLeft } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { BAlert, BButton } from "bootstrap-vue";
import Vue, { computed, onMounted, ref } from "vue";
import { browseRemoteFiles, getFileSources, } from "@/api/remoteFiles";
import { UrlTracker } from "@/components/DataDialog/utilities";
import { isSubPath } from "@/components/FilesDialog/utilities";
import { selectionStates } from "@/components/SelectionDialog/selectionStates";
import { useConfig } from "@/composables/config";
import { errorMessageAsString } from "@/utils/simple-error";
import { Model } from "./model";
import DataDialogSearch from "@/components/SelectionDialog/DataDialogSearch.vue";
import DataDialogTable from "@/components/SelectionDialog/DataDialogTable.vue";
import SelectionDialog from "@/components/SelectionDialog/SelectionDialog.vue";
export default /*#__PURE__*/ _defineComponent({
    __name: 'FilesDialog',
    props: {
        multiple: { type: Boolean, default: false },
        mode: { default: "file" },
        requireWritable: { type: Boolean, default: false },
        filterOptions: { default: undefined },
        callback: { type: Function, default: () => { } }
    },
    setup(__props) {
        const props = __props;
        library.add(faCaretLeft);
        const { config, isConfigLoaded } = useConfig();
        const selectionModel = ref(new Model({ multiple: props.multiple }));
        const allSelected = ref(false);
        const selectedDirectories = ref([]);
        const errorMessage = ref();
        const filter = ref();
        const items = ref([]);
        const modalShow = ref(true);
        const optionsShow = ref(false);
        const undoShow = ref(false);
        const hasValue = ref(false);
        const showTime = ref(true);
        const showDetails = ref(true);
        const isBusy = ref(false);
        const currentDirectory = ref();
        const showFTPHelper = ref(false);
        const selectAllIcon = ref(selectionStates.unselected);
        const urlTracker = ref(new UrlTracker(""));
        const fileMode = computed(() => props.mode == "file");
        const okButtonDisabled = computed(() => (fileMode.value && !hasValue.value) || isBusy.value || (!fileMode.value && urlTracker.value.atRoot()));
        /** Collects selected datasets in value array **/
        function clicked(record) {
            // ignore the click during directory mode
            if (!fileMode.value) {
                return;
            }
            if (record.isLeaf) {
                // record is file
                selectSingleRecord(record);
            }
            else {
                // you cannot select entire root directory
                urlTracker.value.atRoot() ? open(record) : selectDirectoryRecursive(record);
            }
            formatRows();
        }
        function selectSingleRecord(record, selectOnly = false) {
            const selected = selectionModel.value.exists(record.id);
            if (selected) {
                unselectPath(record.url, true);
            }
            if (selectOnly) {
                if (!selected) {
                    selectionModel.value.add(record);
                }
            }
            else {
                selectionModel.value.add(record);
            }
            hasValue.value = selectionModel.value.count() > 0;
            if (props.multiple) {
                formatRows();
            }
            else {
                finalize();
            }
        }
        function unselectPath(path, unselectOnlyAboveDirectories = false, unselectId) {
            // unselect directories
            selectedDirectories.value = selectedDirectories.value.filter((directory) => {
                if (unselectId === directory.id) {
                    return false;
                }
                let matched;
                if (unselectOnlyAboveDirectories) {
                    matched = isSubPath(directory.url, path);
                }
                else {
                    // unselect all folders under or above the current path
                    matched = isSubPath(directory.url, path) || isSubPath(path, directory.url);
                }
                // filter out those that matched
                return !matched;
            });
            // unselect files
            if (!unselectOnlyAboveDirectories) {
                // unselect all files under this path
                selectionModel.value.unselectUnderPath(path);
            }
        }
        function selectDirectoryRecursive(record) {
            // if directory is `selected` or `mixed` unselect everything
            if (isDirectorySelected(record.id) || selectionModel.value.pathExists(record.url)) {
                unselectPath(record.url, false, record.id);
            }
            else {
                selectedDirectories.value.push(record);
                // look for subdirectories
                const recursive = true;
                isBusy.value = true;
                browseRemoteFiles(record.url, recursive).then((items) => {
                    items.forEach((item) => {
                        // construct record
                        const subRecord = entryToRecord(item);
                        if (subRecord.isLeaf) {
                            // select file under this path
                            selectSingleRecord(subRecord, true);
                        }
                        else {
                            // select subdirectory
                            selectedDirectories.value.push(subRecord);
                        }
                    });
                    isBusy.value = false;
                });
            }
        }
        /** Add highlighting for record variations, i.e. datasets vs. libraries/collections **/
        function formatRows() {
            var _a;
            const getIcon = (isSelected, path) => {
                if (isSelected) {
                    return selectionStates.selected;
                }
                else {
                    return selectionModel.value.pathExists(path) ? selectionStates.mixed : selectionStates.unselected;
                }
            };
            hasValue.value = selectionModel.value.count() > 0 || selectedDirectories.value.length > 0;
            for (const item of items.value) {
                let _rowVariant = "active";
                if (item.isLeaf || !fileMode.value) {
                    _rowVariant = selectionModel.value.exists(item.id) ? "success" : "default";
                }
                // if directory
                else if (!item.isLeaf) {
                    _rowVariant = getIcon(isDirectorySelected(item.id), item.url);
                }
                Vue.set(item, "_rowVariant", _rowVariant);
            }
            allSelected.value = checkIfAllSelected();
            if ((_a = currentDirectory.value) === null || _a === void 0 ? void 0 : _a.url) {
                selectAllIcon.value = getIcon(allSelected.value, currentDirectory.value.url);
            }
        }
        function isDirectorySelected(directoryId) {
            return selectedDirectories.value.some(({ id }) => id === directoryId);
        }
        /** check if all objects in this folders are selected **/
        function checkIfAllSelected() {
            const isAllSelected = Boolean(items.value.length) &&
                items.value.every(({ id }) => selectionModel.value.exists(id) || isDirectorySelected(id));
            if (isAllSelected && currentDirectory.value && !isDirectorySelected(currentDirectory.value.id)) {
                // if all selected, select current folder
                selectedDirectories.value.push(currentDirectory.value);
            }
            return isAllSelected;
        }
        function open(record) {
            load(record);
        }
        /** Performs server request to retrieve data records **/
        function load(record) {
            var _a;
            currentDirectory.value = urlTracker.value.getUrl(record);
            showFTPHelper.value = (record === null || record === void 0 ? void 0 : record.url) === "gxftp://";
            filter.value = undefined;
            optionsShow.value = false;
            undoShow.value = !urlTracker.value.atRoot();
            if (urlTracker.value.atRoot() || errorMessage.value) {
                errorMessage.value = undefined;
                getFileSources(props.filterOptions)
                    .then((results) => {
                    const convertedItems = results
                        .filter((item) => !props.requireWritable || item.writable)
                        .map(fileSourcePluginToRecord);
                    items.value = convertedItems;
                    formatRows();
                    optionsShow.value = true;
                    showTime.value = false;
                    showDetails.value = true;
                })
                    .catch((error) => {
                    errorMessage.value = errorMessageAsString(error);
                });
            }
            else {
                if (!currentDirectory.value) {
                    return;
                }
                if (props.mode === "source") {
                    // In source mode, only show sources, not contents
                    items.value = [];
                    optionsShow.value = true;
                    showTime.value = false;
                    showDetails.value = false;
                    return;
                }
                browseRemoteFiles((_a = currentDirectory.value) === null || _a === void 0 ? void 0 : _a.url, false, props.requireWritable)
                    .then((results) => {
                    items.value = filterByMode(results).map(entryToRecord);
                    formatRows();
                    optionsShow.value = true;
                    showTime.value = true;
                    showDetails.value = false;
                })
                    .catch((error) => {
                    errorMessage.value = errorMessageAsString(error);
                });
            }
        }
        function filterByMode(results) {
            if (!fileMode.value) {
                // In directory mode, only show directories
                return results.filter((item) => item.class === "Directory");
            }
            return results;
        }
        function entryToRecord(entry) {
            const result = {
                id: entry.uri,
                label: entry.name,
                time: entry.class === "File" ? entry.ctime : "",
                details: entry.class === "File" ? entry.ctime : "",
                isLeaf: entry.class === "File",
                url: entry.uri,
                labelTitle: entry.uri,
                size: entry.class === "File" ? entry.size : 0,
            };
            return result;
        }
        function fileSourcePluginToRecord(plugin) {
            const result = {
                id: plugin.id,
                label: plugin.label,
                details: plugin.doc,
                isLeaf: false,
                url: plugin.uri_root,
                labelTitle: plugin.uri_root,
            };
            return result;
        }
        /** select all files in current folder**/
        function toggleSelectAll() {
            if (!currentDirectory.value) {
                return;
            }
            const isUnselectAll = selectionModel.value.pathExists(currentDirectory.value.url);
            for (const item of items.value) {
                if (isUnselectAll) {
                    if (selectionModel.value.exists(item.id) || selectionModel.value.pathExists(item.id)) {
                        item.isLeaf ? selectionModel.value.add(item) : selectDirectoryRecursive(item);
                    }
                }
                else {
                    item.isLeaf ? selectionModel.value.add(item) : selectDirectoryRecursive(item);
                }
            }
            if (!isUnselectAll && items.value.length !== 0) {
                selectedDirectories.value.push(currentDirectory.value);
            }
            else if (isDirectorySelected(currentDirectory.value.id)) {
                selectDirectoryRecursive(currentDirectory.value);
            }
            hasValue.value = selectionModel.value.count() > 0;
            formatRows();
        }
        /** Called when selection is complete, values are formatted and parsed to external callback **/
        function finalize() {
            const results = selectionModel.value.finalize();
            modalShow.value = false;
            props.callback(results);
        }
        function goBack() {
            // Loading without a record navigates back one level
            load();
        }
        function onOk() {
            if (!fileMode.value && currentDirectory.value) {
                selectSingleRecord(currentDirectory.value);
            }
            finalize();
        }
        onMounted(() => {
            load();
        });
        return { __sfc: true, props, config, isConfigLoaded, selectionModel, allSelected, selectedDirectories, errorMessage, filter, items, modalShow, optionsShow, undoShow, hasValue, showTime, showDetails, isBusy, currentDirectory, showFTPHelper, selectAllIcon, urlTracker, fileMode, okButtonDisabled, clicked, selectSingleRecord, unselectPath, selectDirectoryRecursive, formatRows, isDirectorySelected, checkIfAllSelected, open, load, filterByMode, entryToRecord, fileSourcePluginToRecord, toggleSelectAll, finalize, goBack, onOk, FontAwesomeIcon, BAlert, BButton, DataDialogSearch, DataDialogTable, SelectionDialog };
    }
});
